import { useMemo, useState, type FormEvent } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import {
  ArrowLeft,
  ArrowRight,
  Calendar,
  Check,
  MessageSquare,
} from 'lucide-react';
import {
  Builder,
  Eyebrow,
  HandwritingReveal,
  MarginNote,
  MarkerCircle,
  MarkerHighlight,
  MarkerUnderline,
  RaceBib,
  Tom,
} from '@/components/foundry';
import { useConvexMutation, useConvexQuery } from '../lib/useConvex';

// Weekly Review is The Lab's Friday cadence:
// one signal, one bottleneck, one next move. It focuses support and
// carry-forward, not analytics scoring or route/product selection.

interface LatestCadenceShape {
  signalSummary: string | null;
  bottleneckClassification:
    | 'support_answer'
    | 'return_to_acquisition'
    | 'return_to_experience'
    | null;
  primaryNextMove: string | null;
  weekNumber: number | null;
  year: number | null;
  updatedAt: string | null;
}

type Bottleneck = NonNullable<LatestCadenceShape['bottleneckClassification']>;
type BottleneckTone = 'race' | 'teal' | 'coral';

interface BottleneckOption {
  value: Bottleneck;
  label: string;
  blurb: string;
  tone: BottleneckTone;
}

const BOTTLENECK_OPTIONS: BottleneckOption[] = [
  {
    value: 'support_answer',
    label: 'I need help',
    blurb: 'I am stuck and need a clear answer before I move.',
    tone: 'teal',
  },
  {
    value: 'return_to_acquisition',
    label: 'Find more buyers',
    blurb: 'The issue is leads, booked calls, conversion, or follow-up.',
    tone: 'race',
  },
  {
    value: 'return_to_experience',
    label: 'Improve delivery',
    blurb: 'The issue is client results, member progress, or proof.',
    tone: 'coral',
  },
];

const FALLBACK_LATEST: LatestCadenceShape = {
  signalSummary:
    'Two good-fit prospects asked for proof before booking. The page is getting attention, but the proof line feels soft.',
  bottleneckClassification: 'support_answer',
  primaryNextMove: 'Ask The Lab to review the proof claim before Monday.',
  weekNumber: 3,
  year: 2026,
  updatedAt: new Date().toISOString(),
};

export default function WeeklyReview() {
  const prefersReducedMotion = useReducedMotion();
  const previewMode = isPreviewMode();
  const latest = useConvexQuery<LatestCadenceShape | null>(
    'labCadence:getLatestLabCadence',
    {},
    { skip: previewMode },
  );
  const submit = useConvexMutation<
    {
      signalSummary: string;
      bottleneckClassification: string;
      primaryNextMove: string;
    },
    LatestCadenceShape
  >('labCadence:recordWeeklyCadence');

  const [signal, setSignal] = useState('');
  const [bottleneck, setBottleneck] = useState<Bottleneck | null>(null);
  const [nextMove, setNextMove] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const lastCadence = latest.data ?? FALLBACK_LATEST;
  const valid = signal.trim().length >= 10 && nextMove.trim().length >= 6 && bottleneck !== null;

  const weekLabel = useMemo(() => {
    const now = new Date();
    return now.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }, []);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setLocalError(null);
    if (!valid || !bottleneck) {
      setLocalError('Add one signal, pick one bottleneck, and name one next move.');
      return;
    }

    if (previewMode) {
      setSubmitted(true);
      return;
    }

    try {
      await submit.mutate({
        signalSummary: signal.trim(),
        bottleneckClassification: bottleneck,
        primaryNextMove: nextMove.trim(),
      });
      setSubmitted(true);
      latest.refresh();
    } catch (error) {
      setLocalError((error as Error).message);
    }
  }

  return (
    <div className="mx-auto max-w-[1080px] px-6 pb-24 pt-10 sm:px-10 sm:pt-14">
      <BackHome />
      <ProductHeader weekLabel={weekLabel} />
      <Hero reducedMotion={Boolean(prefersReducedMotion)} />

      <main className="mt-12 grid gap-10 lg:grid-cols-[minmax(0,690px)_260px] lg:items-start">
        <div className="min-w-0">
          {!submitted ? (
            <CadenceForm
              signal={signal}
              bottleneck={bottleneck}
              nextMove={nextMove}
              localError={localError}
              submitError={submit.error?.message ?? null}
              pending={submit.pending}
              valid={valid}
              onSignalChange={setSignal}
              onBottleneckChange={setBottleneck}
              onNextMoveChange={setNextMove}
              onSubmit={handleSubmit}
            />
          ) : (
            <SubmittedCard
              reducedMotion={Boolean(prefersReducedMotion)}
              onAnother={() => {
                setSignal('');
                setBottleneck(null);
                setNextMove('');
                setSubmitted(false);
              }}
            />
          )}
        </div>

        <aside className="lg:sticky lg:top-24">
          <FridayFocusPanel
            latest={lastCadence}
            loading={latest.loading && !previewMode}
            nextMove={submitted ? nextMove : null}
            reducedMotion={Boolean(prefersReducedMotion)}
          />
        </aside>
      </main>

      <SupportFooter reducedMotion={Boolean(prefersReducedMotion)} />
    </div>
  );
}

function BackHome() {
  return (
    <a
      href="?osRoute=lab-home"
      className="group inline-flex items-center gap-1.5 font-geist-mono text-[11px] font-bold uppercase tracking-[0.06em] text-fd-text-muted transition-colors hover:text-fd-text"
    >
      <ArrowLeft className="h-3.5 w-3.5 transition-transform group-hover:-translate-x-0.5" strokeWidth={2} />
      All products
    </a>
  );
}

function ProductHeader({ weekLabel }: { weekLabel: string }) {
  return (
    <header className="mt-3">
      <div className="flex flex-wrap items-center gap-2.5">
        <span style={{ transform: 'rotate(-2deg)', display: 'inline-block' }}>
          <RaceBib n="W1" tone="race" size="sm" shadow="sm" />
        </span>
        <Eyebrow>
          The Lab{' '}
          <span className="font-caveat text-[18px] font-medium normal-case tracking-normal text-fd-teal">
            - weekly review
          </span>
        </Eyebrow>
        <span className="inline-flex h-5 items-center rounded-[3px] border border-fd-border bg-fd-paper px-1.5 font-geist-mono text-[9.5px] font-bold uppercase leading-none tracking-[0.06em] text-fd-text-subtle">
          Friday · {weekLabel}
        </span>
      </div>
    </header>
  );
}

function Hero({ reducedMotion }: { reducedMotion: boolean }) {
  return (
    <section className="relative mt-8 sm:mt-10">
      <div className="relative grid items-end gap-x-10 gap-y-8 lg:grid-cols-[1fr_250px]">
        <div className="min-w-0">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="font-geist-mono text-[11px] font-semibold uppercase tracking-[0.10em] text-fd-text-subtle">
              One signal · one bottleneck · one next move
            </span>
            <span
              className="font-caveat text-[20px] font-medium leading-none text-fd-coral"
              style={{ transform: 'rotate(-3deg)', display: 'inline-block' }}
            >
              pick one
            </span>
          </div>

          <h1 className="mt-3 [text-wrap:balance]">
            <span className="block font-geist text-[28px] font-medium leading-[1.05] tracking-[-0.015em] text-fd-text-muted sm:text-[36px]">
              Name this week&apos;s
            </span>
            <span className="mt-1 inline-block pr-3">
              <MarkerUnderline tone="race" strokeWidth={5} duration={900} delay={1500}>
                <HandwritingReveal
                  duration={1.25}
                  delay={0.18}
                  className="font-caveat text-[72px] font-medium leading-[0.88] tracking-normal text-fd-text sm:text-[110px]"
                  style={{ transform: 'rotate(-1.5deg)', transformOrigin: 'left center', paddingRight: '0.14em' }}
                >
                  bottleneck
                </HandwritingReveal>
              </MarkerUnderline>
              <span className="font-geist text-[40px] font-medium text-fd-text sm:text-[60px]">.</span>
            </span>
          </h1>

          <p className="mt-5 max-w-[600px] text-[16px] leading-[1.55] text-fd-text-muted [text-wrap:pretty]">
            Tell us what changed and what you want to do next. Keep it useful for the thing that
            actually affects leads, calls, clients, or proof.
          </p>

          <div className="mt-7 flex flex-wrap items-center gap-x-4 gap-y-3">
            <a
              href="#weekly-review"
              className="inline-flex h-11 items-center gap-2 rounded-[6px] border-[1.5px] border-fd-text bg-fd-accent px-5 font-geist-mono text-[12.5px] font-bold uppercase tracking-[0.04em] text-fd-text transition-transform hover:-translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fd-text"
              style={{ boxShadow: '2px 2px 0 0 var(--fd-text)' }}
            >
              Submit review
              <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.25} />
            </a>
            <a
              href="?osRoute=lab"
              className="inline-flex h-10 items-center gap-1.5 rounded-[5px] border border-fd-border bg-fd-paper px-3 font-geist-mono text-[11.5px] font-bold uppercase tracking-[0.04em] text-fd-text-muted transition-colors hover:border-fd-text/60 hover:text-fd-text"
            >
              Ask The Lab
              <MessageSquare className="h-3.5 w-3.5" strokeWidth={2} />
            </a>
          </div>
        </div>

        <motion.div
          aria-hidden
          initial={reducedMotion ? false : { opacity: 0, y: 10 }}
          animate={reducedMotion ? undefined : { opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
          className="relative ml-auto mr-3 w-[118px] max-w-[38vw] justify-self-end lg:mr-0 lg:w-full lg:max-w-none"
        >
          <MarginNote tilt={6} tone="teal" size="sm" className="absolute -top-4 left-0 hidden lg:block">
            clear beats complete.
          </MarginNote>
          <Tom
            variant="pointing-right"
            className="block w-full drop-shadow-[0_18px_18px_rgba(22,18,10,0.18)]"
          />
        </motion.div>
      </div>
    </section>
  );
}

function CadenceForm({
  signal,
  bottleneck,
  nextMove,
  localError,
  submitError,
  pending,
  valid,
  onSignalChange,
  onBottleneckChange,
  onNextMoveChange,
  onSubmit,
}: {
  signal: string;
  bottleneck: Bottleneck | null;
  nextMove: string;
  localError: string | null;
  submitError: string | null;
  pending: boolean;
  valid: boolean;
  onSignalChange: (value: string) => void;
  onBottleneckChange: (value: Bottleneck) => void;
  onNextMoveChange: (value: string) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}) {
  return (
    <form
      id="weekly-review"
      onSubmit={onSubmit}
      className="relative border-y border-fd-border"
    >
      <div className="divide-y divide-fd-border">
        <CadenceSection
          num="01"
          label="What changed?"
          helper="One useful signal from the week."
        >
          <textarea
            value={signal}
            onChange={(event) => onSignalChange(event.target.value)}
            rows={4}
            placeholder="e.g. Three prospects asked for proof before booking. The page is getting attention, but the proof line feels too soft."
            className="w-full resize-none rounded-[6px] border border-fd-border bg-fd-paper p-3.5 font-geist text-[14px] leading-[1.55] text-fd-text outline-none transition-colors placeholder:text-fd-text-subtle/70 focus:border-fd-text"
          />
          <p className="mt-1.5 font-geist-mono text-[10px] font-semibold uppercase tracking-[0.06em] text-fd-text-subtle">
            {signal.trim().length} chars · min 10
          </p>
        </CadenceSection>

        <CadenceSection
          num="02"
          label="What is stuck?"
          helper="Pick the one that fits best."
        >
          <div className="grid gap-2">
            {BOTTLENECK_OPTIONS.map((option) => (
              <BottleneckButton
                key={option.value}
                option={option}
                selected={bottleneck === option.value}
                onClick={() => onBottleneckChange(option.value)}
              />
            ))}
          </div>
        </CadenceSection>

        <CadenceSection
          num="03"
          label="Next move"
          helper="One thing you can actually do."
        >
          <input
            value={nextMove}
            onChange={(event) => onNextMoveChange(event.target.value)}
            maxLength={140}
            placeholder="Ask The Lab to review the proof claim, then update the headline."
            className="h-11 w-full rounded-[6px] border border-fd-border bg-fd-paper px-3.5 font-geist text-[14px] text-fd-text outline-none transition-colors placeholder:text-fd-text-subtle/70 focus:border-fd-text"
          />
          <p className="mt-1.5 font-geist-mono text-[10px] font-semibold uppercase tracking-[0.06em] text-fd-text-subtle">
            {nextMove.length}/140
          </p>
        </CadenceSection>
      </div>

      {(localError || submitError) ? (
        <p className="mt-5 rounded-[8px] border border-fd-coral/40 bg-fd-coral-soft px-3.5 py-2.5 text-[13px] leading-[1.5] text-fd-text">
          {localError ?? submitError}
        </p>
      ) : null}

      <div className="flex flex-col gap-4 py-5 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <MarginNote tilt={-3} tone="teal" size="sm" className="inline-block">
            keep the path simple.
          </MarginNote>
        </div>
        <button
          type="submit"
          disabled={!valid || pending}
          className="inline-flex h-11 items-center justify-center gap-2 rounded-[6px] border-[1.5px] border-fd-text bg-fd-accent px-5 font-geist-mono text-[12.5px] font-bold uppercase tracking-[0.04em] text-fd-text transition-transform hover:-translate-y-px disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fd-text"
          style={{ boxShadow: '2px 2px 0 0 var(--fd-text)' }}
        >
          {pending ? 'Submitting...' : 'Submit review'}
          <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.25} />
        </button>
      </div>
    </form>
  );
}

function CadenceSection({
  num,
  label,
  helper,
  children,
}: {
  num: string;
  label: string;
  helper: string;
  children: React.ReactNode;
}) {
  return (
    <fieldset className="grid gap-4 px-5 py-5 sm:grid-cols-[190px_1fr] sm:gap-8 sm:px-6">
      <legend className="sr-only">{label}</legend>
      <div className="flex gap-3">
        <span
          className="grid h-9 w-9 shrink-0 place-items-center border-[1.5px] border-fd-text bg-fd-paper font-geist-mono text-[13px] font-bold text-fd-text"
          style={{ transform: 'rotate(-2deg)' }}
        >
          {num}
        </span>
        <div>
          <h2 className="font-geist text-[17px] font-medium tracking-[-0.01em] text-fd-text">
            {label}
          </h2>
          <p className="mt-1 text-[12.5px] leading-[1.45] text-fd-text-muted">{helper}</p>
        </div>
      </div>
      <div className="min-w-0">{children}</div>
    </fieldset>
  );
}

function BottleneckButton({
  option,
  selected,
  onClick,
}: {
  option: BottleneckOption;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cls(
        'relative grid gap-2 rounded-[6px] border p-3.5 text-left transition-colors sm:grid-cols-[1fr_auto] sm:items-center',
        selected
          ? 'border-fd-text bg-fd-accent text-fd-text'
          : 'border-fd-border bg-fd-paper text-fd-text hover:bg-fd-surface-1',
      )}
      style={selected ? { boxShadow: '2px 2px 0 0 var(--fd-text)' } : undefined}
    >
      <span className="min-w-0">
        <span className="flex flex-wrap items-center gap-2">
          <span className="font-geist text-[13.5px] font-medium">{option.label}</span>
          {selected ? (
            <MarkerCircle tone={option.tone} strokeWidth={2} duration={700} delay={100} padding={4}>
              <span className="inline-flex h-5 items-center rounded-[3px] border border-fd-text bg-fd-paper px-1.5 font-geist-mono text-[9.5px] font-bold uppercase leading-none tracking-[0.06em] text-fd-text">
                Picked
              </span>
            </MarkerCircle>
          ) : null}
        </span>
        <span className="mt-1 block text-[12.25px] leading-[1.45] text-fd-text-muted">{option.blurb}</span>
      </span>
      <span className={cls(
        'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border',
        selected ? 'border-fd-text bg-fd-text text-fd-accent' : 'border-fd-border text-fd-text-subtle',
      )}>
        {selected ? <Check className="h-3.5 w-3.5" strokeWidth={2.5} /> : null}
      </span>
    </button>
  );
}

function FridayFocusPanel({
  latest,
  loading,
  nextMove,
  reducedMotion,
}: {
  latest: LatestCadenceShape;
  loading: boolean;
  nextMove: string | null;
  reducedMotion: boolean;
}) {
  return (
    <section className="border-l border-fd-border pl-5">
      <Eyebrow className="mb-2">Friday focus</Eyebrow>
      <h2 className="font-geist text-[20px] font-medium tracking-[-0.015em] text-fd-text">
        Keep one thread.
      </h2>
      <p className="mt-2 text-[13px] leading-[1.5] text-fd-text-muted">
        Works whether the issue is booked calls, clinic leads, client results, or proof.
      </p>

      <div className="mt-7 space-y-5">
        <div>
          <p className="font-caveat text-[20px] font-medium text-fd-coral">
            {loading ? 'checking last week...' : `last week · wk ${latest.weekNumber ?? '-'}`}
          </p>
          <div className="mt-3 space-y-4">
            <ReadoutBlock label="Signal" value={latest.signalSummary ?? 'No signal recorded yet.'} />
            <ReadoutBlock label="Move" value={latest.primaryNextMove ?? 'No next move recorded yet.'} />
          </div>
        </div>

        <div className="border-t border-fd-border pt-5">
          <p className="text-[13px] leading-[1.5] text-fd-text-muted">
            Tom checks your review when a founder read would help. The Lab handles the rest.
          </p>
          <motion.div
            aria-hidden
            initial={reducedMotion ? false : { opacity: 0, y: 8 }}
            animate={reducedMotion ? undefined : { opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: 0.15 }}
            className="mt-4 w-[92px] sm:w-[118px]"
          >
            <Tom
              variant="listening"
              className="block w-full drop-shadow-[0_18px_18px_rgba(22,18,10,0.18)]"
            />
          </motion.div>
        </div>

        <div className="border-t border-fd-border pt-5">
          <Eyebrow className="mb-1.5">Command Centre</Eyebrow>
          <p className="text-[13px] leading-[1.5] text-fd-text">
            {nextMove?.trim() || 'Your next move appears there after you submit.'}
          </p>
        </div>
      </div>
    </section>
  );
}

function SubmittedCard({
  reducedMotion,
  onAnother,
}: {
  reducedMotion: boolean;
  onAnother: () => void;
}) {
  return (
    <motion.section
      initial={reducedMotion ? false : { opacity: 0, y: 10 }}
      animate={reducedMotion ? undefined : { opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className="relative grid grid-cols-1 gap-6 rounded-[14px] border-[1.5px] border-fd-text bg-fd-paper p-7 lg:grid-cols-[1fr_auto]"
      style={{ boxShadow: '4px 4px 0 0 var(--fd-text)' }}
    >
      <div>
        <RaceBib n="" label="Review saved" tone="race" size="md" shadow="sm" className="mb-3" />
        <h2 className="font-geist text-[24px] font-medium leading-[1.15] tracking-[-0.02em] text-fd-text">
          Saved. The week has a spine.
        </h2>
        <p className="mt-2 max-w-[480px] text-[13.5px] leading-[1.55] text-fd-text-muted">
          The Lab has enough to aim support at the thing that matters this week.
        </p>
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <a
            href="?osRoute=lab-home"
            className="inline-flex h-10 items-center gap-2 rounded-[5px] border-[1.5px] border-fd-text bg-fd-accent px-4 font-geist-mono text-[12px] font-bold uppercase tracking-[0.04em] text-fd-text transition-transform hover:-translate-y-px"
            style={{ boxShadow: '2px 2px 0 0 var(--fd-text)' }}
          >
            <Calendar className="h-3.5 w-3.5" strokeWidth={2} />
            Back to Lab home
          </a>
          <button
            type="button"
            onClick={onAnother}
            className="inline-flex h-10 items-center gap-1.5 rounded-[5px] border border-fd-border bg-fd-paper px-3.5 font-geist text-[13px] font-medium text-fd-text-muted transition-colors hover:border-fd-text/60 hover:text-fd-text"
          >
            Edit answer
          </button>
        </div>
      </div>
      <div className="flex shrink-0 items-end justify-end">
        <Builder variant="aha" className="h-[116px] w-auto drop-shadow-[0_18px_18px_rgba(22,18,10,0.16)] lg:h-[148px]" />
      </div>
    </motion.section>
  );
}

function SupportFooter({ reducedMotion }: { reducedMotion: boolean }) {
  return (
    <footer className="relative mt-16 flex flex-col gap-4 border-t border-fd-border pt-7 sm:flex-row sm:items-end sm:justify-between">
      <div className="relative flex-1">
        <p className="text-[14.5px] leading-[1.5] text-fd-text [text-wrap:pretty]">
          If you are stuck,{' '}
          <MarkerHighlight tone="teal" duration={700} delay={200}>
            <span className="font-medium">Ask The Lab</span>
          </MarkerHighlight>{' '}
          before choosing a harder next move.
        </p>
        <MarginNote tilt={-2} tone="ink" size="sm" className="mt-2 inline-block opacity-80">
          included during month one.
        </MarginNote>
      </div>

      <div className="flex items-center gap-4">
        <a
          href="?osRoute=lab"
          className="inline-flex h-10 items-center gap-1.5 self-start rounded-[5px] border-[1.5px] border-fd-text bg-fd-paper px-4 font-geist-mono text-[11.5px] font-bold uppercase tracking-[0.04em] text-fd-text transition-colors hover:bg-fd-accent hover:text-[oklch(0.16_0.005_95)] sm:self-auto"
          style={{ boxShadow: '2px 2px 0 0 var(--fd-text)' }}
        >
          <MessageSquare className="h-3.5 w-3.5" strokeWidth={2} />
          Ask The Lab
        </a>

        <motion.div
          aria-hidden
          initial={reducedMotion ? false : { opacity: 0, y: 10 }}
          animate={reducedMotion ? undefined : { opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
          className="h-[74px] w-[74px] sm:h-[88px] sm:w-[88px]"
        >
          <Builder
            variant="reading-notebook"
            className="block h-full w-auto drop-shadow-[0_16px_16px_rgba(22,18,10,0.14)]"
          />
        </motion.div>
      </div>
    </footer>
  );
}

function ReadoutBlock({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="font-geist-mono text-[10px] font-semibold uppercase tracking-[0.06em] text-fd-text-subtle">
        {label}
      </p>
      <p className="mt-1 text-[12.75px] leading-[1.5] text-fd-text-muted">{value}</p>
    </div>
  );
}

function isPreviewMode(): boolean {
  if (typeof window === 'undefined') return false;
  const preview = new URLSearchParams(window.location.search).get('preview');
  return preview === 'member' || preview === 'admin';
}

function cls(...parts: Array<string | false | undefined>): string {
  return parts.filter(Boolean).join(' ');
}
